import { use, useMemo } from 'react'
import { RBACContext } from '@/components/rbac/context'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { useReadQuery } from '@/lib/hooks'
import { RBAC } from '@/lib/rbac'

export const RBACProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    userCompaniesQueryRef,
    userPermissionsQueryRef,
    totalServicesQueryRef,
  } = use(QueryRefsContext)

  const { data: companiesData } = useReadQuery(userCompaniesQueryRef)
  const { data: permissionsData } = useReadQuery(userPermissionsQueryRef)
  const { data: totalServicesData } = useReadQuery(totalServicesQueryRef)

  const idCompanies = useMemo(() => {
    const companies = companiesData?.user?.companies || []
    return companies.map((company: { id: string }) => company.id)
  }, [companiesData])

  const permissions = useMemo(
    () => permissionsData?.user?.permissions || [],
    [permissionsData]
  )

  const rbac = useMemo(
    () => RBAC.createWithPermissions(permissions),
    [permissions]
  )

  const rolesInCompany = useMemo(() => {
    return permissionsData?.user?.rolesInCompany || []
  }, [permissionsData])

  const totalServices = useMemo(() => {
    const company = totalServicesData?.company

    return {
      totalApps: company?.apps?.total || 0,
      totalPipelines: company?.pipelines?.total || 0,
      totalDatabases: company?.databases?.total || 0,
      totalStaticSites: company?.staticSites?.total || 0,
      totalObjectStorages: company?.objectStorages?.total || 0,
      totalLoadBalancers: company?.edgeLoadBalancers?.total || 0,
      totalProjects: company?.projects?.total || 0,
    }
  }, [totalServicesData])

  return (
    <RBACContext.Provider
      value={{ idCompanies, rbac, totalServices, rolesInCompany }}
    >
      {children}
    </RBACContext.Provider>
  )
}
